import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export interface CommandPaletteItem {
  name: string;
  href: string;
  icon: React.FC<any>;
  groupName?: string;
}

interface CommandPaletteProps {
  items: CommandPaletteItem[];
  isOpen: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ items, isOpen, onClose }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filteredItems = useMemo(() => {
    if (!query.trim()) return items;
    const search = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return items.filter(item => {
      const name = t(item.name).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const group = item.groupName ? t(item.groupName).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
      return name.includes(search) || group.includes(search);
    });
  }, [query, items, t]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selectedEl = listRef.current.children[selectedIndex] as HTMLElement;
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredItems.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = filteredItems[selectedIndex];
        if (item?.href) {
          navigate(item.href);
          onClose();
        }
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedIndex, navigate, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] sm:pt-[15vh]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col max-h-[70vh]">
        {/* Search input header */}
        <div className="flex items-center px-4 py-3.5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('nav.commandPalette.placeholder')}
            className="flex-1 ml-3 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-base"
          />
          <kbd className="hidden sm:inline-block px-2 py-0.5 text-xs font-mono text-gray-500 bg-gray-100 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
            ESC
          </kbd>
        </div>

        {/* Results list */}
        <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
          {filteredItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
              <p className="text-sm">{t('nav.commandPalette.noResults')}</p>
              {query.trim() && (
                <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
                  {t('nav.commandPalette.tryDifferent')}
                </p>
              )}
            </div>
          ) : (
            <div className="py-2">
              {filteredItems.map((item, index) => {
                const isSelected = index === selectedIndex;
                return (
                  <button
                    key={`${item.groupName || 'root'}-${item.name}`}
                    onClick={() => {
                      navigate(item.href);
                      onClose();
                    }}
                    className={`w-full flex items-center px-4 py-2.5 text-left transition-all duration-150 ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-indigo-100 dark:bg-indigo-800/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                      <item.icon className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'}`} />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isSelected ? 'text-indigo-900 dark:text-indigo-100' : 'text-gray-700 dark:text-gray-200'}`}>
                        {t(item.name)}
                      </div>
                      {item.groupName && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                          {t(item.groupName)}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <kbd className="ml-2 px-1.5 py-0.5 text-xs font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 flex-shrink-0">
                        ↵
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer shortcuts */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 mr-1.5 font-mono">↑↓</kbd>
              <span className="hidden sm:inline">{t('nav.commandPalette.navigate')}</span>
            </span>
            <span className="flex items-center">
              <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 mr-1.5 font-mono">↵</kbd>
              <span className="hidden sm:inline">{t('nav.commandPalette.select')}</span>
            </span>
          </div>
          <span className="flex items-center">
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 mr-1.5 font-mono">ESC</kbd>
            <span className="hidden sm:inline">{t('nav.commandPalette.close')}</span>
          </span>
        </div>
      </div>
    </div>
  );
};
